import { Model } from '@/clients/http'

import { ProductVariationHttpParams, ProductVariationResponses } from '@/types'

export class ProductVariationModel extends Model {
  // Define correlation keys for Redux state management
  public static readonly correlations = {
    CREATE: 'CREATE_PRODUCT_VARIATION',
    UPDATE: 'UPDATE_PRODUCT_VARIATION',
    DELETE: 'DELETE_PRODUCT_VARIATION',
  }

  // Define endpoint builders
  public static readonly endpoints = {
    getList: (props?: ProductVariationHttpParams.GetList) =>
      this.createEndpoint({
        url: '/api/backoffice/product/:productId/variation',
        params: props?.params,
        query: props?.query,
      }),

    getById: (props?: ProductVariationHttpParams.GetById) =>
      this.createEndpoint({
        url: '/api/backoffice/product/:productId/variation/:variationId',
        params: props?.params,
        query: props?.query,
      }),

    create: (props?: ProductVariationHttpParams.Create) =>
      this.createEndpoint({
        url: '/api/backoffice/product/:productId/variation',
        params: props?.params,
        query: props?.query,
      }),

    update: (props?: ProductVariationHttpParams.Update) =>
      this.createEndpoint({
        url: '/api/backoffice/product/:productId/variation/:variationId',
        params: props?.params,
        query: props?.query,
      }),

    delete: (props?: ProductVariationHttpParams.Delete) =>
      this.createEndpoint({
        url: '/api/backoffice/product/:productId/variation/:variationId',
        params: props?.params,
        query: props?.query,
      }),

    generateKeywords: (props?: ProductVariationHttpParams.GenerateKeywords) =>
      this.createEndpoint({
        url: '/api/backoffice/product/:productId/variation/:variationId/generate-keywords',
        params: props?.params,
      }),
  }

  /**
   * Get list of variations for a product
   */
  public static readonly getList = async (props: ProductVariationHttpParams.GetList) => {
    return this.createHttpRequest<
      ProductVariationResponses.GetList,
      ProductVariationResponses.GetListError
    >({
      correlationKey: 'GET_PRODUCT_VARIATION_LIST',
      payload: {
        method: 'get',
        url: this.endpoints.getList(props),
        useBearerToken: true,
      },
      store: props?.store,
    })
  }

  /**
   * Get single variation
   */
  public static readonly getById = async (props: ProductVariationHttpParams.GetById) => {
    return this.createHttpRequest<
      ProductVariationResponses.GetById,
      ProductVariationResponses.GetByIdError
    >({
      correlationKey: 'GET_PRODUCT_VARIATION_BY_ID',
      payload: {
        method: 'get',
        url: this.endpoints.getById(props),
        useBearerToken: true,
      },
      store: props?.store,
    })
  }

  /**
   * Create a new product variation
   */
  public static readonly create = async (props: ProductVariationHttpParams.Create) => {
    return this.createHttpRequest<
      ProductVariationResponses.Create,
      ProductVariationResponses.CreateError
    >({
      correlationKey: this.correlations.CREATE,
      payload: {
        method: 'post',
        url: this.endpoints.create(props),
        body: props.body,
        useBearerToken: true,
      },
      store: props?.store,
    })
  }

  /**
   * Update an existing product variation
   */
  public static readonly update = async (props: ProductVariationHttpParams.Update) => {
    return this.createHttpRequest<
      ProductVariationResponses.Update,
      ProductVariationResponses.UpdateError
    >({
      correlationKey: this.correlations.UPDATE,
      payload: {
        method: 'put',
        url: this.endpoints.update(props),
        body: props.body,
        useBearerToken: true,
      },
      store: props?.store,
    })
  }

  /**
   * Generate AI search keyword suggestions for a variation
   */
  public static readonly generateKeywords = async (
    props: ProductVariationHttpParams.GenerateKeywords
  ) => {
    return this.createHttpRequest<
      ProductVariationResponses.GenerateKeywords,
      ProductVariationResponses.GenerateKeywordsError
    >({
      correlationKey: 'GENERATE_PRODUCT_VARIATION_KEYWORDS',
      payload: {
        method: 'post',
        url: this.endpoints.generateKeywords(props),
        useBearerToken: true,
      },
      store: props?.store,
    })
  }

  /**
   * Delete a product variation
   */
  public static readonly delete = async (props: ProductVariationHttpParams.Delete) => {
    return this.createHttpRequest<
      ProductVariationResponses.Delete,
      ProductVariationResponses.DeleteError
    >({
      correlationKey: this.correlations.DELETE,
      payload: {
        method: 'delete',
        url: this.endpoints.delete(props),
        useBearerToken: true,
      },
      store: props?.store,
    })
  }
}
